<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Otso Panalo Game</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Bets</h2>

    <v-card class="shadow">
      <v-card-title>
        <v-row justify="space-between">
          <v-col cols="12" sm="6" md="4" xl="2" align-self="end">
            <div class="d-flex align-center">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="secondary"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="loading"
                    @click="loadRecords"
                  >
                    <v-icon>mdi-cached</v-icon>
                  </v-btn>
                </template>
                <span>Refresh</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="secondary"
                    v-bind="attrs"
                    v-on="on"
                    :loading="exportLoading"
                    @click="exportToCSV"
                  >
                    <v-icon>mdi-file-delimited-outline</v-icon>
                  </v-btn>
                </template>
                <span>Export to CSV</span>
              </v-tooltip>

              <v-btn
                class="ml-2"
                color="primary"
                outlined
                :disabled="loading"
                @click="openFilterDialog"
              >
                <v-icon left>mdi-filter-variant</v-icon>
                Filters
              </v-btn>
            </div>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            md="4"
            xl="3"
            class="d-flex justify-end align-end"
          >
            <v-chip color="primary" outlined pill>
              Total: {{ totalRows }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search username, ticket, combination, or game"
              hide-details="auto"
              outlined
              dense
              clearable
              @keydown.enter.prevent="applySearch"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <div
              v-if="hasActiveFilters"
              class="d-flex flex-wrap justify-md-end"
              style="gap: 8px"
            >
              <v-chip v-if="dateFrom" small outlined color="primary">
                {{ filterBy }} From: {{ dateFrom }}
              </v-chip>
              <v-chip v-if="dateTo" small outlined color="primary">
                {{ filterBy }} To: {{ dateTo }}
              </v-chip>
              <v-chip v-if="selectedGameId" small outlined color="primary">
                Game: {{ selectedGameLabel }}
              </v-chip>
              <v-chip v-if="search" small outlined color="primary">
                Search: {{ search }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="records"
          :loading="loading"
          hide-default-footer
          loading-text="Loading... Please wait"
          class="elevation-0"
        >
          <template v-slot:[`item.game`]="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.league || "-" }}</div>
              <div>
                #{{ item.ending_game_id || "-" }}
                <span class="text-overline my-2 mx-2 font-italic">|</span>
                {{ item.team_a_name }} {{ item.team_a_alias }}
                <span class="text-overline my-2 mx-2 font-italic">vs</span>
                {{ item.team_b_name }} {{ item.team_b_alias }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.bet_type`]="{ item }">
            <span>{{ item.bet_type || "-" }}</span>
          </template>

          <template v-slot:[`item.combinations`]="{ item }">
            <div class="d-flex flex-column py-2">
              <div
                v-for="quarter in quarterRows(item)"
                :key="quarter.label"
                class="d-flex flex-wrap align-center"
                style="gap: 8px"
              >
                <span>{{ quarter.label }} Bet: {{ quarter.bet || "-" }}</span>
                <span>Result: {{ quarter.result || "-" }}</span>
                <v-chip
                  x-small
                  class="font-weight-medium"
                  :class="quarterStatusClass(quarter.status)"
                >
                  {{ quarter.status || "-" }}
                </v-chip>
              </div>
            </div>
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span>{{ moneyFormat(item.amount) }}</span>
          </template>

          <template v-slot:[`item.prize_amount`]="{ item }">
            <span>{{ moneyFormat(item.prize_amount) }}</span>
          </template>

          <template v-slot:[`item.refunded`]="{ item }">
            <v-chip
              small
              class="font-weight-medium"
              :class="
                item.refunded === 'Yes'
                  ? 'red lighten-5 red--text'
                  : 'green lighten-5 green--text'
              "
            >
              {{ item.refunded }}
            </v-chip>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span>{{ formatTheDate(item.created_at) }}</span>
          </template>

          <template v-slot:no-data>
            <div class="pa-6 text-center grey--text">
              No bets found for the selected filters.
            </div>
          </template>
        </v-data-table>

        <div class="pa-4">
          <v-row class="d-flex flex-row-reverse">
            <v-col cols="12" sm="4" md="3" lg="2">
              <v-select
                v-model="itemsPerPage"
                :items="itemsPerPageOptions"
                label="Rows per page"
                outlined
                dense
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>

          <div class="d-flex justify-center">
            <v-pagination
              v-model="currentPage"
              :disabled="loading"
              :length="totalPage"
              :total-visible="7"
            ></v-pagination>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="filterDialog" max-width="960" persistent>
      <v-card>
        <v-card-title class="text-h6 font-weight-medium">
          Filter Bets
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="applyFilters"
          >
            <v-row dense>
              <v-col cols="12" md="6" class="mt-3">
                <v-select
                  v-model="dialogFilters.filterBy"
                  :items="filterByOptions"
                  :rules="[rules.required]"
                  label="Filter By"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>

              <v-col cols="12" md="6" class="mt-3">
                <v-select
                  v-model="dialogFilters.selectedGameId"
                  :items="gameOptions"
                  item-text="label"
                  item-value="id"
                  label="Ending Game"
                  dense
                  outlined
                  hide-details="auto"
                  clearable
                  :loading="gamesLoading"
                />
              </v-col>

              <v-col cols="12" md="6" class="mt-3">
                <v-menu
                  v-model="dateFromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dialogFilters.dateFrom"
                      :rules="[validDateRangeFrom]"
                      :label="`${dialogFilters.filterBy} From`"
                      hide-details="auto"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dialogFilters.dateFrom"
                    @input="dateFromMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="6" class="mt-3">
                <v-menu
                  v-model="dateToMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dialogFilters.dateTo"
                      :rules="[validDateRangeTo]"
                      :label="`${dialogFilters.filterBy} To (Optional)`"
                      hide-details="auto"
                      readonly
                      outlined
                      dense
                      clearable
                      :disabled="!dialogFilters.dateFrom"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dialogFilters.dateTo"
                    :min="dialogFilters.dateFrom || ''"
                    @input="dateToMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="loading" @click="closeFilterDialog">
            Cancel
          </v-btn>
          <v-btn text :disabled="loading" @click="resetFilters"> Reset </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!valid || loading"
            @click="applyFilters"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formatDate from "@/helpers/formatDate";
import responseGet from "@/helpers/api_request_get";

export default {
  data: () => ({
    valid: true,
    loading: false,
    exportLoading: false,
    gamesLoading: false,
    filterDialog: false,
    filterByOptions: ["Bet Date", "Game Schedule"],
    filterBy: "Bet Date",
    dateFrom: "",
    dateFromMenu: false,
    dateTo: "",
    dateToMenu: false,
    selectedGameId: null,
    search: "",
    dialogFilters: {
      filterBy: "Bet Date",
      dateFrom: "",
      dateTo: "",
      selectedGameId: null,
    },
    records: [],
    gameOptions: [],
    currentPage: 1,
    totalPage: 0,
    totalRows: 0,
    itemsPerPage: 10,
    itemsPerPageOptions: [10, 20, 50, 100],
    headers: [
      { text: "ID", value: "id", class: "text-uppercase" },
      {
        text: "Player Username",
        value: "username",
        class: "text-uppercase",
        sortable: false,
      },
      { text: "Game", value: "game", class: "text-uppercase", sortable: false },
      {
        text: "Bet Type",
        value: "bet_type",
        class: "text-uppercase",
        sortable: false,
      },
      {
        text: "Ticket Name",
        value: "ticket_name",
        class: "text-uppercase",
        sortable: false,
      },
      {
        text: "Combinations",
        value: "combinations",
        class: "text-uppercase",
        sortable: false,
      },
      {
        text: "Amount",
        value: "amount",
        class: "text-uppercase",
        align: "right",
      },
      {
        text: "Prize Amount",
        value: "prize_amount",
        class: "text-uppercase",
        align: "right",
        sortable: false,
      },
      { text: "Refunded", value: "refunded", class: "text-uppercase" },
      { text: "Bet At", value: "created_at", class: "text-uppercase" },
    ],
  }),

  computed: {
    hasActiveFilters() {
      return !!(
        this.dateFrom ||
        this.dateTo ||
        this.selectedGameId ||
        this.search
      );
    },

    exportExcelFields() {
      return {
        ID: "id",
        "Player Username": "username",
        Game: {
          callback: (value) => {
            const league = value.league || "-";
            const gameId = value.ending_game_id || "-";
            const teams = `${value.team_a_name || ""} ${value.team_a_alias || ""} vs ${value.team_b_name || ""} ${value.team_b_alias || ""}`;
            return `${league} | #${gameId} | ${teams}`;
          },
        },
        "Bet Type": "bet_type",
        "Ticket Name": "ticket_name",
        "Q1 Bet": "first_quarter_combination",
        "Q1 Result": "first_quarter_result",
        "Q1 Status": "first_quarter_status",
        "Q2 Bet": "second_quarter_combination",
        "Q2 Result": "second_quarter_result",
        "Q2 Status": "second_quarter_status",
        "Q3 Bet": "third_quarter_combination",
        "Q3 Result": "third_quarter_result",
        "Q3 Status": "third_quarter_status",
        "Q4 Bet": "fourth_quarter_combination",
        "Q4 Result": "fourth_quarter_result",
        "Q4 Status": "fourth_quarter_status",
        Amount: {
          callback: (value) => {
            return "PHP " + this.moneyFormat(value.amount);
          },
        },
        "Prize Amount": {
          callback: (value) => {
            return "PHP " + this.moneyFormat(value.prize_amount);
          },
        },
        Refunded: "refunded",
        "Bet At": {
          callback: (value) => {
            return this.formatTheDate(value.created_at);
          },
        },
      };
    },

    selectedGameLabel() {
      const selectedGame = this.gameOptions.find(
        (option) => option.id === this.selectedGameId,
      );

      return selectedGame ? selectedGame.label : this.selectedGameId;
    },

    rules() {
      return {
        required: (value) => !!value || "This field is required.",
      };
    },

    validDateRangeFrom() {
      if (
        this.dialogFilters.dateFrom &&
        this.dialogFilters.dateTo &&
        new Date(this.dialogFilters.dateFrom) >
          new Date(this.dialogFilters.dateTo)
      ) {
        return "Must be less than or equal to the end date.";
      }

      return true;
    },

    validDateRangeTo() {
      if (
        this.dialogFilters.dateFrom &&
        this.dialogFilters.dateTo &&
        new Date(this.dialogFilters.dateTo) <
          new Date(this.dialogFilters.dateFrom)
      ) {
        return "Must be greater than or equal to the start date.";
      }

      return true;
    },
  },

  created() {
    this.applyQueryState();
    this.initialize();
  },

  methods: {
    formatTheDate(value) {
      return formatDate(value, true);
    },

    moneyFormat(value) {
      const amount = Number(value || 0);

      return `PHP ${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    quarterRows(item) {
      return [
        {
          label: "Q1",
          bet: item.first_quarter_combination,
          result: item.first_quarter_result,
          status: item.first_quarter_status,
        },
        {
          label: "Q2",
          bet: item.second_quarter_combination,
          result: item.second_quarter_result,
          status: item.second_quarter_status,
        },
        {
          label: "Q3",
          bet: item.third_quarter_combination,
          result: item.third_quarter_result,
          status: item.third_quarter_status,
        },
        {
          label: "Q4",
          bet: item.fourth_quarter_combination,
          result: item.fourth_quarter_result,
          status: item.fourth_quarter_status,
        },
      ];
    },

    quarterStatusClass(status) {
      if (status === "Win") {
        return "green lighten-5 green--text";
      }

      if (status === "Lose") {
        return "red lighten-5 red--text";
      }

      return "grey lighten-3 grey--text text--darken-1";
    },

    buildQueryString() {
      const params = new URLSearchParams();

      params.append("page", this.currentPage);
      params.append("itemsPerPage", this.itemsPerPage);

      if (this.filterBy) {
        params.append("filterBy", this.filterBy);
      }

      if (this.dateFrom) {
        params.append("dateFrom", this.dateFrom);
      }

      if (this.dateTo) {
        params.append("dateTo", this.dateTo);
      }

      if (this.selectedGameId) {
        params.append("gameId", this.selectedGameId);
      }

      if (this.search) {
        params.append("search", this.search);
      }

      return params.toString();
    },

    async initialize() {
      await this.loadGameOptions();
      await this.loadRecords();
    },

    applyQueryState() {
      const { query } = this.$route;

      if (query.filterBy) {
        this.filterBy = query.filterBy;
      }

      if (query.dateFrom) {
        this.dateFrom = query.dateFrom;
      }

      if (query.dateTo) {
        this.dateTo = query.dateTo;
      }

      if (query.gameId) {
        this.selectedGameId = Number(query.gameId);
      }

      if (query.search) {
        this.search = query.search;
      }

      this.syncDialogFilters();

      if (query.page) {
        this.currentPage = Number(query.page) || 1;
      }

      if (query.itemsPerPage) {
        this.itemsPerPage = Number(query.itemsPerPage) || 10;
      }
    },

    syncQueryState() {
      const query = {
        page: String(this.currentPage),
        itemsPerPage: String(this.itemsPerPage),
      };

      if (this.filterBy) {
        query.filterBy = this.filterBy;
      }

      if (this.dateFrom) {
        query.dateFrom = this.dateFrom;
      }

      if (this.dateTo) {
        query.dateTo = this.dateTo;
      }

      if (this.selectedGameId) {
        query.gameId = String(this.selectedGameId);
      }

      if (this.search) {
        query.search = this.search;
      }

      this.$router.replace({ name: "OtsoPanaloBets", query }).catch(() => {});
    },

    async loadGameOptions() {
      try {
        this.gamesLoading = true;

        const response = await responseGet("", "/otso_panalo/games");

        if (response.status === 200) {
          const records = response.data.records || [];

          this.gameOptions = records.map((record) => ({
            id: record.id,
            label: `#${record.ending_game_id} | ${record.league} | ${record.team_a_name} ${record.team_a_alias} vs ${record.team_b_name} ${record.team_b_alias}`,
          }));
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.$store.commit(
            "setRefreshError",
            "Failed to load Otso Panalo games.",
          );
        }
      } catch (error) {
        this.$store.commit(
          "setRefreshError",
          "Failed to load Otso Panalo games.",
        );
      } finally {
        this.gamesLoading = false;
      }
    },

    openFilterDialog() {
      this.syncDialogFilters();
      this.filterDialog = true;
    },

    closeFilterDialog() {
      this.syncDialogFilters();
      this.filterDialog = false;
    },

    syncDialogFilters() {
      this.dialogFilters = {
        filterBy: this.filterBy,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        selectedGameId: this.selectedGameId,
      };
    },

    async applySearch() {
      this.currentPage = 1;
      await this.loadRecords();
    },

    async loadRecords() {
      try {
        this.loading = true;
        this.syncQueryState();

        const response = await responseGet(
          this.buildQueryString(),
          "/otso_panalo/bets",
        );

        if (response.status === 200) {
          this.records = response.data.records || [];
          this.totalRows = response.data.totalRows || 0;
          this.totalPage = response.data.totalPage || 0;

          if (this.totalPage > 0 && this.currentPage > this.totalPage) {
            this.currentPage = this.totalPage;
            await this.loadRecords();
          }
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.records = [];
          this.totalRows = 0;
          this.totalPage = 0;
          this.$store.commit(
            "setRefreshError",
            "Failed to load Otso Panalo bets.",
          );
        }
      } catch (error) {
        this.records = [];
        this.totalRows = 0;
        this.totalPage = 0;
        this.$store.commit(
          "setRefreshError",
          "Failed to load Otso Panalo bets.",
        );
      } finally {
        this.loading = false;
      }
    },

    async applyFilters() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.filterBy = this.dialogFilters.filterBy;
      this.dateFrom = this.dialogFilters.dateFrom;
      this.dateTo = this.dialogFilters.dateFrom
        ? this.dialogFilters.dateTo
        : "";
      this.selectedGameId = this.dialogFilters.selectedGameId;

      this.currentPage = 1;
      this.closeFilterDialog();
      await this.loadRecords();
    },

    async resetFilters() {
      this.filterBy = "Bet Date";
      this.dateFrom = "";
      this.dateTo = "";
      this.selectedGameId = null;
      this.search = "";
      this.syncDialogFilters();
      this.currentPage = 1;
      this.itemsPerPage = 10;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.closeFilterDialog();
      await this.loadRecords();
    },

    async exportToCSV() {
      try {
        this.exportLoading = true;

        const params = new URLSearchParams();

        if (this.filterBy) {
          params.append("filterBy", this.filterBy);
        }

        if (this.dateFrom) {
          params.append("dateFrom", this.dateFrom);
        }

        if (this.dateTo) {
          params.append("dateTo", this.dateTo);
        }

        if (this.selectedGameId) {
          params.append("gameId", this.selectedGameId);
        }

        if (this.search) {
          params.append("search", this.search);
        }

        params.append("page", "1");
        params.append("itemsPerPage", this.totalRows || "100000");
        params.append("export", "1");

        const response = await responseGet(
          params.toString(),
          "/otso_panalo/bets",
        );

        if (response.status === 200) {
          const records = response.data.records || [];
          const csv = this.generateCSV(records);
          this.downloadCSV(csv);
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.$store.commit(
            "setRefreshError",
            "Failed to export Otso Panalo bets.",
          );
        }
      } catch (error) {
        this.$store.commit(
          "setRefreshError",
          "Failed to export Otso Panalo bets.",
        );
      } finally {
        this.exportLoading = false;
      }
    },

    generateCSV(records) {
      const fields = this.exportExcelFields;
      const headers = Object.keys(fields);
      const BOM = "\uFEFF";

      const escapeCSVValue = (val) => {
        const str = String(val ?? "");
        const escaped = str.replace(/"/g, '""');
        return `"${escaped}"`;
      };

      const csvRows = [BOM + headers.map((h) => escapeCSVValue(h)).join(",")];

      for (const record of records) {
        const row = headers.map((header) => {
          const field = fields[header];
          let value;

          if (typeof field === "string") {
            value = record[field] ?? "";
          } else if (typeof field === "object" && field.callback) {
            value = field.callback(record) ?? "";
          } else {
            value = "";
          }

          const strValue = String(value);

          // Prevent Excel from interpreting values like "1-2" or "3-4"
          // as dates by prefixing with a tab character
          if (/^\d{1,2}[-/]\d{1,2}/.test(strValue)) {
            return escapeCSVValue("\t" + strValue);
          }

          return escapeCSVValue(strValue);
        });

        csvRows.push(row.join(","));
      }

      return csvRows.join("\n");
    },

    downloadCSV(csv) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp =
        `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
        `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
      const filename = `Otso Panalo Bets ${timestamp}.csv`;

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },

  watch: {
    currentPage(value, oldValue) {
      if (value && value !== oldValue) {
        this.loadRecords();
      }
    },

    itemsPerPage(value, oldValue) {
      if (value && value !== oldValue) {
        this.currentPage = 1;
        this.loadRecords();
      }
    },

    "dialogFilters.dateFrom"(value) {
      if (!value) {
        this.dialogFilters.dateTo = "";
      }
    },
  },
};
</script>
